<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>UGC Purchases</span>

            <v-btn small color="primary" @click="loadPurchases" :loading="loading">
              Refresh
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-alert
              v-if="error"
              type="error"
              dense
              outlined
              class="mb-4"
            >
              {{ error }}
            </v-alert>

            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  label="Søg fil, virksomhed eller influencer"
                  outlined
                  dense
                  clearable
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filterStatus"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Status"
                  outlined
                  dense
                />
              </v-col>
            </v-row>

            <v-simple-table v-if="filteredPurchases.length">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Aktiv</th>
                  <th>Status</th>
                  <th>Virksomhed</th>
                  <th>Influencer</th>
                  <th>Total</th>
                  <th>Basis pris</th>
                  <th>Kommission</th>
                  <th>Rettigheder</th>
                  <th>Rettighedsperiode</th>
                  <th>Oprettet</th>
                  <th>Betalt</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="purchase in filteredPurchases" :key="purchase.id">
                  <td>{{ purchase.id }}</td>
                  <td>{{ getAssetName(purchase) }}</td>
                  <td>
                    <v-chip x-small dark :color="getStatusColor(purchase.status)">
                      {{ formatStatusLabel(purchase) }}
                    </v-chip>
                  </td>
                  <td>{{ getBusinessName(purchase) }}</td>
                  <td>{{ getInfluencerName(purchase) }}</td>
                  <td>{{ formatMinorAmount(purchase.total_price, purchase.currency) }}</td>
                  <td>{{ formatMinorAmount(purchase.base_price, purchase.currency) }}</td>
                  <td>{{ formatMinorAmount(purchase.commission_amount, purchase.currency) }} ({{ purchase.commission_percent }}%)</td>
                  <td>{{ formatUsageRights(purchase.ugcAsset ? purchase.ugcAsset.usage_rights : null) }}</td>
                  <td>{{ formatRightsPeriod(purchase.ugcAsset ? purchase.ugcAsset.rights_period : null) }}</td>
                  <td>{{ purchase.created_at }}</td>
                  <td>{{ purchase.paid_at || "-" }}</td>
                  <td>
                    <v-btn
                      x-small
                      color="error"
                      :loading="deletingPurchaseId === purchase.id"
                      @click="removePurchase(purchase)"
                    >
                      Slet
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <div v-else class="text--secondary">
              Ingen UGC køb fundet.
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";

const UGC_PURCHASE_STATUS = {
  PENDING: 0,
  CHECKOUT_CREATED: 1,
  PAID: 2,
  FAILED: 3,
  REFUNDED: 4,
};

export default {
  name: "ModeratorUgcPurchases",

  data() {
    return {
      loading: false,
      deletingPurchaseId: null,
      error: "",
      purchases: [],
      search: "",
      filterStatus: "all",
      ugcPurchaseStatus: UGC_PURCHASE_STATUS,
      statusOptions: [
        { text: "Alle", value: "all" },
        { text: "Afventer", value: UGC_PURCHASE_STATUS.PENDING },
        { text: "Checkout oprettet", value: UGC_PURCHASE_STATUS.CHECKOUT_CREATED },
        { text: "Betalt", value: UGC_PURCHASE_STATUS.PAID },
      ],
    };
  },

  computed: {
    filteredPurchases() {
      let items = this.purchases || [];

      if (this.filterStatus !== "all") {
        items = items.filter(item => Number(item.status) === Number(this.filterStatus));
      }

      if (this.search) {
        const q = this.search.toLowerCase();

        items = items.filter(item => {
          const asset = this.getAssetName(item).toLowerCase();
          const business = this.getBusinessName(item).toLowerCase();
          const influencer = this.getInfluencerName(item).toLowerCase();

          return (
            asset.includes(q) ||
            business.includes(q) ||
            influencer.includes(q)
          );
        });
      }

      return items;
    },
  },

  created() {
    this.loadPurchases();
  },

  methods: {
    ...mapActions("core", [
      "getModeratorUgcPurchases",
      "deleteUgcPurchase",
    ]),

    async loadPurchases() {
      this.loading = true;
      this.error = "";

      try {
        this.purchases = await this.getModeratorUgcPurchases();
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Kunne ikke indlæse UGC køb.";
      } finally {
        this.loading = false;
      }
    },

    getAssetName(purchase) {
      if (purchase.ugcAsset && purchase.ugcAsset.original_filename) {
        return purchase.ugcAsset.original_filename;
      }

      return "Asset #" + purchase.ugc_asset_id;
    },

    getBusinessName(purchase) {
      if (
        purchase.ugcAsset &&
        purchase.ugcAsset.business &&
        purchase.ugcAsset.business.display_name
      ) {
        return purchase.ugcAsset.business.display_name;
      }

      if (
        purchase.ugcAsset &&
        purchase.ugcAsset.business &&
        purchase.ugcAsset.business.company
      ) {
        return purchase.ugcAsset.business.company;
      }

      return "Business #" + purchase.business_id;
    },

    getInfluencerName(purchase) {
      if (
        purchase.influencer &&
        purchase.influencer.user &&
        (purchase.influencer.user.first_name || purchase.influencer.user.last_name)
      ) {
        return `${purchase.influencer.user.first_name || ""} ${purchase.influencer.user.last_name || ""}`.trim();
      }

      return "Influencer #" + purchase.influencer_id;
    },

    getStatusColor(status) {
        const numericStatus = Number(status);

        if (numericStatus === this.ugcPurchaseStatus.PAID) return "success";
        if (numericStatus === this.ugcPurchaseStatus.CHECKOUT_CREATED) return "warning";
        if (numericStatus === this.ugcPurchaseStatus.PENDING) return "info";
        return "grey";
    },

    async removePurchase(purchase) {
      const confirmed = window.confirm(
        `Delete purchase #${purchase.id}?`
      );

      if (!confirmed) {
        return;
      }

      this.deletingPurchaseId = purchase.id;
      this.error = "";

      try {
        await this.deleteUgcPurchase({
          ugc_purchase_id: purchase.id,
        });

        this.purchases = this.purchases.filter(
          item => String(item.id) !== String(purchase.id)
        );
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Could not delete purchase.";
      } finally {
        this.deletingPurchaseId = null;
      }
    },

    formatStatusLabel(purchase) {
        const label = purchase.status_label;

        if (!label) {
            return purchase.status;
        }

        if (label === "checkout_created") return "Checkout oprettet";
        if (label === "paid") return "Betalt";
        if (label === "pending") return "Afventer";
        if (label === "failed") return "Fejlede";
        if (label === "refunded") return "Refunderet";

        return label;
    },

    formatMinorAmount(value, currency = "dkk") {
      if (value === null || value === undefined) {
        return "-";
      }

      return `${(value / 100).toFixed(2)} ${String(currency).toUpperCase()}`;
    },

    formatUsageRights(value) {
      if (!value || !value.length) return "-";
      const map = {
        social_media:    "Sociale medier",
        paid_ads:        "Paid ads",
        website:         "Website",
        email_marketing: "Email marketing",
        landing_pages:   "Landingpages",
        print_outdoor:   "Print / outdoor",
      };
      return value.map(v => map[v] || v).join(", ");
    },

    formatRightsPeriod(value) {
      const map = {
        "30":        "30 dage",
        "90":        "90 dage",
        "180":       "6 måneder",
        "365":       "12 måneder",
        "unlimited": "Ubegrænset",
      };
      return map[value] || value || "-";
    },
  },
};
</script>